import { CopyIcon } from '@phosphor-icons/react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * A single icon-only copy affordance, shared.
 *
 * Before this existed, 15+ files each hand-rolled their own
 * `navigator.clipboard.writeText` call behind their own button
 * (`generate-document-view.tsx`, `PostRevenueCard.tsx`, `DocumentBody.tsx`,
 * `TeamSettings.tsx`, `MediaGridItem.tsx`, `ImageGenerationResult.tsx`, and
 * more) — same icon, same toast convention, same shape, copy-pasted rather
 * than shared. This is the one place that behaviour lives now. Extend it
 * (new `reveal` mode, richer clipboard flavours) rather than forking a
 * sixteenth copy button.
 *
 * Existing call sites are NOT retrofitted onto this by this change — that's
 * a separate sweep, and several of those files belong to other in-flight
 * work.
 */
export interface CopyButtonProps {
  /** Plain-text flavour. Always written. */
  getText: () => string;
  /** Optional richer flavour, e.g. a table's outerHTML, so spreadsheets paste real cells. */
  getHtml?: () => string;
  /** Accessible name + tooltip. Defaults to 'Copy'. */
  label?: string;
  /** Toast on success. Defaults to 'Copied'. */
  successMessage?: string;
  /** 'hover' = invisible until the closest `group` is hovered (default). 'always' = always visible. */
  reveal?: 'hover' | 'always';
  className?: string;
}

export function CopyButton({
  getText,
  getHtml,
  label = 'Copy',
  successMessage = 'Copied',
  reveal = 'hover',
  className,
}: CopyButtonProps) {
  const handleClick = async () => {
    const text = getText();
    try {
      const html = getHtml?.();
      if (html && typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/html': new Blob([html], { type: 'text/html' }),
            'text/plain': new Blob([text], { type: 'text/plain' }),
          }),
        ]);
      } else {
        await navigator.clipboard.writeText(text);
      }
      toast.success(successMessage);
    } catch {
      toast.error('Could not copy');
    }
  };

  return (
    <Button
      type="button"
      variant="flat"
      size="icon-sm"
      aria-label={label}
      title={label}
      onClick={handleClick}
      className={cn(
        reveal === 'hover' &&
          'opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100',
        className,
      )}
    >
      <CopyIcon className="size-3.5" weight="regular" />
    </Button>
  );
}
